import { MapPin, Star, Phone, Globe, DollarSign, ExternalLink } from "lucide-react";
import type { ToolWidgetProps } from "./index";

interface Place {
  google_place_id: string;
  name: string;
  formatted_address?: string;
  short_address?: string;
  latitude?: number;
  longitude?: number;
  types?: string[];
  primary_type?: string;
  primary_type_display?: string;
  phone?: string;
  website?: string;
  google_maps_url?: string;
  business_status?: string;
  rating?: number;
  rating_count?: number;
  price_level?: number;
  description?: string;
}

interface GooglePlacesSearchData {
  results: Place[];
  count: number;
  search_type?: string;
  query?: string;
}

function PriceLevel({ level }: { level: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[...Array(4)].map((_, i) => (
        <DollarSign
          key={i}
          className={`w-3 h-3 ${i < level ? "text-green-600" : "text-slate-300"}`}
        />
      ))}
    </div>
  );
}

export function GooglePlacesSearchWidget({
  data,
  count,
}: ToolWidgetProps<GooglePlacesSearchData>) {
  const { results = [] } = data || {};

  if (!results.length) {
    return (
      <div className="text-sm text-slate-500 italic">Aucun lieu trouvé</div>
    );
  }

  return (
    <div className="space-y-2">
      {results.map((place) => (
        <div
          key={place.google_place_id}
          className="flex flex-col gap-2 p-3 bg-white rounded-lg border border-slate-200 hover:border-slate-300 transition-colors"
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="font-medium text-slate-800 truncate">
                {place.name}
              </div>
              {place.primary_type_display && (
                <div className="text-xs text-slate-500 truncate">
                  {place.primary_type_display}
                </div>
              )}
            </div>
            {place.rating && (
              <div className="flex items-center gap-1 flex-shrink-0">
                <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                <span className="text-sm font-medium text-slate-700">
                  {place.rating.toFixed(1)}
                </span>
                {place.rating_count && (
                  <span className="text-xs text-slate-400">
                    ({place.rating_count})
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
            {(place.formatted_address || place.short_address) && (
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                <span className="truncate max-w-xs">
                  {place.short_address || place.formatted_address}
                </span>
              </span>
            )}
            {place.price_level && <PriceLevel level={place.price_level} />}
            {place.phone && (
              <span className="flex items-center gap-1">
                <Phone className="w-3 h-3" />
                {place.phone}
              </span>
            )}
            {place.website && (
              <a
                href={place.website}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-default-600 hover:text-default-700"
              >
                <Globe className="w-3 h-3" />
                Site web
              </a>
            )}
            {place.google_maps_url && (
              <a
                href={place.google_maps_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-default-600 hover:text-default-700"
              >
                <ExternalLink className="w-3 h-3" />
                Maps
              </a>
            )}
          </div>
        </div>
      ))}
      {count && count > results.length && (
        <div className="text-xs text-slate-400 text-center pt-1">
          + {count - results.length} autres lieux
        </div>
      )}
    </div>
  );
}
